// src/components/CreateUser.js
// Nuovo utente (redesign 2026-10, stesso stile del pannello admin). Solo aspetto: stessi campi,
// stessi obbligatori, stessa chiamata userService.createUser({ ...userData, credentialMode }) e
// stessi messaggi; l'esito si legge nel pannello admin (prima: alert), dove si torna come prima.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import userService from '../services/userService';
import { FiArrowLeft, FiEye, FiEyeOff, FiKey, FiLink, FiSlash, FiRefreshCw, FiUserPlus } from 'react-icons/fi';
import '../css/AdminPage.css';

const CREDENTIAL_MODES = [
  { value: 'password', icon: FiKey, title: 'Invia email con la password', hint: 'Il cliente riceve email e password per entrare subito.' },
  { value: 'link', icon: FiLink, title: 'Invia email con link per impostare la password', hint: 'Il cliente sceglie la sua password dal link (valido 7 giorni).' },
  { value: 'none', icon: FiSlash, title: 'Non inviare email', hint: 'Le credenziali le comunichi tu.' },
];

const ROLE_OPTIONS = [
  { value: 'user', label: 'User' },
  { value: 'admin', label: 'Admin' },
  { value: 'elettricista', label: 'Elettricista' },
  { value: 'architetto', label: 'Architetto' },
  { value: 'privato', label: 'Privato' },
  { value: 'agenzia', label: 'Agenzia' },
  { value: 'azienda', label: 'Azienda' },
];

const COUNTRY_OPTIONS = [
  { value: 'IT', label: 'Italia' },
  { value: 'AT', label: 'Austria' },
  { value: 'DE', label: 'Germania' },
  { value: 'ES', label: 'Spagna' },
  { value: 'FR', label: 'Francia' },
  { value: 'OTHER', label: 'Altro' },
];

const LANGUAGE_OPTIONS = [
  { value: 'it', label: 'Italiano' },
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
  { value: 'es', label: 'Espanol' },
  { value: 'fr', label: 'Francais' },
];

// Password proposta (solo un aiuto: si può sempre scrivere la propria)
const makePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  const values = new Uint32Array(10);
  const cryptoApi = window.crypto || window.msCrypto;
  if (cryptoApi && cryptoApi.getRandomValues) cryptoApi.getRandomValues(values);
  else values.forEach((_, i) => { values[i] = Math.floor(Math.random() * 4294967296); });
  return Array.from(values, (v) => chars[v % chars.length]).join('');
};

const CreateUser = ({ token }) => {
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    discount: 0,
    phoneNumber: '', // Esempio di campo facoltativo
    address: '', // Esempio di campo facoltativo
    companyName: '',
    country: '',
    preferredLanguage: 'it',
    // Aggiungi altri campi facoltativi qui
  });
  const [showPassword, setShowPassword] = useState(false);
  // Modalità invio credenziali al nuovo cliente: 'password' | 'link' | 'none'
  const [credentialMode, setCredentialMode] = useState('password');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });
  };

  const handleCreateUser = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await userService.createUser(
        { ...userData, credentialMode },
        token
      );
      // Stessi messaggi di prima (erano alert), mostrati nel pannello admin dove si torna.
      let notice;
      if (credentialMode === 'none') {
        notice = { text: 'Utente creato con successo.', kind: 'ok' };
      } else if (response && response.credentialsSent) {
        notice = {
          text: credentialMode === 'link'
            ? 'Utente creato. Email con il link per impostare la password inviata al cliente.'
            : 'Utente creato. Email con le credenziali inviata al cliente.',
          kind: 'ok',
        };
      } else {
        const reason = response && response.credentialsError
          ? ` Dettaglio errore: ${response.credentialsError}.`
          : '';
        notice = {
          text: 'Utente creato, ma l\'invio dell\'email delle credenziali NON è riuscito.' + reason + ' Comunica le credenziali manualmente o riprova.',
          kind: 'err',
          sticky: true,
        };
      }
      navigate('/admin-dashboard', { state: { notice } });
    } catch (err) {
      console.error('Failed to create user:', err);
      setError('Failed to create user');
      setSaving(false);
    }
  };

  const mode = CREDENTIAL_MODES.find((m) => m.value === credentialMode);
  const roleLabel = ROLE_OPTIONS.find((r) => r.value === userData.role)?.label;
  const countryLabel = COUNTRY_OPTIONS.find((c) => c.value === userData.country)?.label;

  return (
    <div className="ad cu">
      <section className="ad-band">
        <div className="ad-head">
          <div>
            <div className="cu-crumb">Pannello admin</div>
            <h1>Nuovo <em>·</em> utente</h1>
            <p>Crea l'accesso al B2B per un cliente e scegli come inviargli le credenziali.</p>
          </div>
          <div className="ad-head-acts">
            <button type="button" className="ad-btn" onClick={() => navigate('/admin-dashboard')}>
              <FiArrowLeft aria-hidden="true" /> Torna al pannello
            </button>
          </div>
        </div>
      </section>

      <form className="cu-grid" onSubmit={(e) => { e.preventDefault(); handleCreateUser(); }}>
        <div className="cu-main">
          <div className="ad-card">
            <h3>Cliente</h3>
            <div className="ad-fg">
              <div className="ad-f">
                <label htmlFor="cu-name">Nome e cognome *</label>
                <input id="cu-name" type="text" name="name" placeholder="Name" value={userData.name} onChange={handleInputChange} required autoComplete="off" />
              </div>
              <div className="ad-f">
                <label htmlFor="cu-company">Azienda *</label>
                <input id="cu-company" type="text" name="companyName" placeholder="Azienda" value={userData.companyName} onChange={handleInputChange} required autoComplete="off" />
              </div>
              <div className="ad-f">
                <label htmlFor="cu-country">Paese *</label>
                <select id="cu-country" name="country" value={userData.country} onChange={handleInputChange} required>
                  <option value="">Country</option>
                  {COUNTRY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="ad-f">
                <label htmlFor="cu-lang">Lingua</label>
                <select id="cu-lang" name="preferredLanguage" value={userData.preferredLanguage} onChange={handleInputChange}>
                  {LANGUAGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="ad-f">
                <label htmlFor="cu-phone">Telefono</label>
                <input id="cu-phone" type="text" name="phoneNumber" placeholder="Phone Number" value={userData.phoneNumber} onChange={handleInputChange} autoComplete="off" />
              </div>
              <div className="ad-f">
                <label htmlFor="cu-address">Indirizzo</label>
                <input id="cu-address" type="text" name="address" placeholder="Address" value={userData.address} onChange={handleInputChange} autoComplete="off" />
              </div>
            </div>
          </div>

          <div className="ad-card">
            <h3>Condizioni commerciali</h3>
            <div className="ad-fg">
              <div className="ad-f">
                <label htmlFor="cu-role">Ruolo *</label>
                <select id="cu-role" name="role" value={userData.role} onChange={handleInputChange} required>
                  <option value="">Select a role</option>
                  {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="ad-f">
                <label htmlFor="cu-discount">Sconto (%)</label>
                <div className="ad-disc-in">
                  <input id="cu-discount" type="number" name="discount" placeholder="Discount" value={userData.discount} onChange={handleInputChange} />
                  <div className="ad-chips" aria-label="Sconti rapidi">
                    {[30, 35, 40, 45, 50].map((d) => (
                      <button
                        key={d}
                        type="button"
                        aria-pressed={Number(userData.discount) === d}
                        onClick={() => handleInputChange({ target: { name: 'discount', value: String(d) } })}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="ad-card">
            <h3>Accesso e credenziali</h3>
            <div className="ad-fg">
              <div className="ad-f">
                <label htmlFor="cu-email">Email (è anche il nome utente) *</label>
                <input id="cu-email" type="email" name="email" placeholder="Email" value={userData.email} onChange={handleInputChange} required autoComplete="off" />
              </div>
              <div className="ad-f">
                <label htmlFor="cu-password">
                  Password {credentialMode === 'link' ? <small>(opzionale, la imposta il cliente)</small> : '*'}
                </label>
                <div className="cu-pass">
                  <input
                    id="cu-password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder={credentialMode === 'link' ? 'Password (opzionale, la imposta il cliente)' : 'Password'}
                    value={userData.password}
                    onChange={handleInputChange}
                    required={credentialMode !== 'link'}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="cu-eye"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                    title={showPassword ? 'Nascondi password' : 'Mostra password'}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                  <button
                    type="button"
                    className="ad-btn is-sm"
                    onClick={() => { handleInputChange({ target: { name: 'password', value: makePassword() } }); setShowPassword(true); }}
                    title="Propone una password casuale"
                  >
                    <FiRefreshCw aria-hidden="true" /> Genera
                  </button>
                </div>
              </div>
            </div>

            <fieldset className="cu-modes">
              <legend>Invio credenziali al cliente</legend>
              {CREDENTIAL_MODES.map((m) => {
                const Icon = m.icon;
                return (
                  <label key={m.value} className={`cu-mode${credentialMode === m.value ? ' is-on' : ''}`}>
                    <input
                      type="radio"
                      name="credentialMode"
                      value={m.value}
                      checked={credentialMode === m.value}
                      onChange={(e) => setCredentialMode(e.target.value)}
                    />
                    <span className="cu-mode-i" aria-hidden="true"><Icon /></span>
                    <span className="cu-mode-t">
                      <b>{m.title}</b>
                      <small>{m.hint}</small>
                    </span>
                  </label>
                );
              })}
            </fieldset>
          </div>
        </div>

        <aside className="cu-side">
          <div className="ad-card cu-sum">
            <h3>Riepilogo</h3>
            <div className="cu-who">
              <span className="ad-av is-lg">{(userData.name || '?').trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase() || '?'}</span>
              <div>
                <b>{userData.name || 'Nuovo cliente'}</b>
                <small>{userData.email || 'email non inserita'}</small>
              </div>
            </div>
            <div className="ad-kv">
              <span>Azienda</span><b>{userData.companyName || '—'}</b>
              <span>Paese</span><b>{countryLabel || '—'}</b>
              <span>Ruolo</span><b>{roleLabel ? <span className={`ad-role is-${userData.role}`}>{roleLabel}</span> : '—'}</b>
              <span>Sconto</span><b className="ad-mono">{userData.discount === '' ? '—' : `${userData.discount}%`}</b>
            </div>
            <div className="cu-mail">
              <mode.icon aria-hidden="true" />
              <span>{mode.title}</span>
            </div>
            {error && <div className="cu-err" role="alert">{error}</div>}
            <button type="submit" className="ad-btn is-primary cu-submit" disabled={saving}>
              <FiUserPlus aria-hidden="true" /> {saving ? 'Creazione…' : 'Crea utente'}
            </button>
            <button type="button" className="ad-btn cu-cancel" onClick={() => navigate('/admin-dashboard')}>Annulla</button>
            <small className="cu-note">* campi obbligatori</small>
          </div>
        </aside>
      </form>
    </div>
  );
};

export default CreateUser;
